/**
 * Impairment Report Review
 *
 * Upload a completed impairment assessment report (DOCX/PDF) and have it
 * checked for AMA arithmetic accuracy, internal consistency, repetition and
 * completeness. Nothing is stored server-side — the review lives for the
 * session only.
 *
 * SECURITY: All fetch calls include the JWT auth token from sessionStorage.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://clinhx-backend-v2.onrender.com/api';

const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = [
  { key: 'accuracy', label: 'Accuracy', hint: 'AMA arithmetic and combined values', color: '#dc2626' },
  { key: 'consistency', label: 'Consistency', hint: 'Narrative vs numbers, dates, laterality', color: '#ea580c' },
  { key: 'repetition', label: 'Repetition', hint: 'Duplicated and restated content', color: '#7c3aed' },
  { key: 'completeness', label: 'Completeness', hint: 'Required sections and placeholders', color: '#0891b2' },
];

const SEVERITY_STYLE = {
  critical: { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', label: 'Critical' },
  major: { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', label: 'Major' },
  minor: { bg: '#f9fafb', border: '#e5e7eb', text: '#4b5563', label: 'Minor' },
  info: { bg: '#f9fafb', border: '#e5e7eb', text: '#4b5563', label: 'Note' },
};

const ACCEPTED = '.pdf,.docx,.doc';

export default function ReportReview() {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState(null); // { jobId, status, progress, message, result, error }
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [dismissed, setDismissed] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const isActive = job && (job.status === 'queued' || job.status === 'processing');

  // Poll for job status while the review is running.
  useEffect(() => {
    if (!isActive || !job?.jobId) return undefined;

    const abortController = new AbortController();
    const jobId = job.jobId;

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/impairment-review/job/${jobId}/status`, {
          headers: getAuthHeaders(),
          signal: abortController.signal,
        });
        if (!res.ok) throw new Error('Could not get review status');
        const status = await res.json();
        if (!abortController.signal.aborted) {
          setJob((prev) => (prev && prev.jobId === jobId ? { ...prev, ...status } : prev));
        }
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Review status poll error:', err);
      }
    };

    poll();
    const interval = setInterval(poll, 4000);
    return () => {
      abortController.abort();
      clearInterval(interval);
    };
  }, [isActive, job?.jobId]);

  const pickFile = (f) => {
    if (!f) return;
    const ext = (f.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      setError('Please upload a PDF or Word document.');
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      setError('File is larger than the 50MB limit.');
      return;
    }
    setError(null);
    setFile(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    pickFile(e.dataTransfer.files?.[0]);
  }, []);

  const startReview = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setDismissed(new Set());
    try {
      const formData = new FormData();
      formData.append('report', file);

      const res = await fetch(`${API_URL}/impairment-review/upload`, {
        method: 'POST',
        headers: getAuthHeaders(), // do NOT set Content-Type; the browser sets the boundary
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      setJob({ jobId: data.jobId, fileName: data.fileName, status: 'queued', progress: 0, message: 'Queued' });
    } catch (err) {
      setError(err.message || 'Failed to start the review');
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setJob(null);
    setFile(null);
    setError(null);
    setDismissed(new Set());
    setCategoryFilter('all');
    setSeverityFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const downloadDocument = async () => {
    if (!job?.jobId) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/impairment-review/job/${job.jobId}/document`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Could not generate the review document');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Review - ${(job.fileName || 'report').replace(/\.[^.]+$/, '')}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const result = job?.status === 'completed' ? job.result : null;
  const findings = useMemo(() => result?.findings || [], [result]);

  const visibleFindings = useMemo(
    () =>
      findings.filter(
        (f) =>
          !dismissed.has(f.id) &&
          (categoryFilter === 'all' || f.category === categoryFilter) &&
          (severityFilter === 'all' || f.severity === severityFilter)
      ),
    [findings, dismissed, categoryFilter, severityFilter]
  );

  const countFor = (category) => findings.filter((f) => f.category === category && !dismissed.has(f.id)).length;
  const severityCount = (sev) => findings.filter((f) => f.severity === sev && !dismissed.has(f.id)).length;

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: '#111827' }}>Impairment Report Review</h1>
        <p style={{ margin: '8px 0 0', fontSize: 14, color: '#6b7280' }}>
          Upload a completed impairment assessment and have it checked for accuracy, consistency, repetition and
          completeness before it is sent.
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: 16, padding: '12px 14px', borderRadius: 8,
            background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* ---------- Upload ---------- */}
      {!job && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragging ? '#2563eb' : '#d1d5db'}`,
              background: dragging ? '#eff6ff' : '#fafafa',
              borderRadius: 10, padding: '40px 20px', textAlign: 'center', cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              style={{ display: 'none' }}
              onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <div style={{ fontSize: 15, fontWeight: 500, color: '#374151' }}>
              {file ? file.name : 'Drop the completed report here, or click to browse'}
            </div>
            <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
              {file
                ? `${(file.size / 1024 / 1024).toFixed(1)} MB — click to choose a different file`
                : 'Word or PDF, up to 50MB'}
            </div>
          </div>

          <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={startReview}
              disabled={!file || uploading}
              style={{
                padding: '10px 20px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: 600,
                background: !file || uploading ? '#d1d5db' : '#2563eb',
                color: '#fff', cursor: !file || uploading ? 'not-allowed' : 'pointer',
              }}
            >
              {uploading ? 'Starting…' : 'Review report'}
            </button>
            {file && !uploading && (
              <button
                onClick={() => setFile(null)}
                style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 14, color: '#6b7280', cursor: 'pointer' }}
              >
                Clear
              </button>
            )}
          </div>

          <p style={{ margin: '16px 0 0', fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
            The report is analysed and discarded — nothing is saved to the patient record or to file storage. Findings
            are a checking aid only; every one must be verified against the source report before acting on it.
          </p>
        </div>
      )}

      {/* ---------- Progress ---------- */}
      {job && job.status !== 'completed' && job.status !== 'failed' && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
            Reviewing {job.fileName}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 14 }}>{job.message || 'Working…'}</div>
          <div style={{ height: 8, background: '#f3f4f6', borderRadius: 999, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%', width: `${job.progress || 0}%`, background: '#2563eb',
                borderRadius: 999, transition: 'width 0.4s ease',
              }}
            />
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: '#9ca3af' }}>
            Large reports can take a couple of minutes. You can leave this page open.
          </div>
        </div>
      )}

      {/* ---------- Failure ---------- */}
      {job?.status === 'failed' && (
        <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#991b1b', marginBottom: 6 }}>Review failed</div>
          <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 16 }}>{job.error || 'Something went wrong.'}</div>
          <button
            onClick={reset}
            style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
          >
            Try another report
          </button>
        </div>
      )}

      {/* ---------- Results ---------- */}
      {result && (
        <>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>{job.fileName}</div>
                <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                  {findings.length === 0
                    ? 'No issues identified.'
                    : `${findings.length} finding${findings.length === 1 ? '' : 's'} — ` +
                      `${severityCount('critical')} critical, ${severityCount('major')} major, ${severityCount('minor')} minor`}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={downloadDocument}
                  disabled={downloading}
                  style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                >
                  {downloading ? 'Preparing…' : 'Download as Word'}
                </button>
                <button
                  onClick={reset}
                  style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Review another
                </button>
              </div>
            </div>

            {/* Rating cross-check */}
            {(result.summary?.statedFinal !== null && result.summary?.statedFinal !== undefined) && (
              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f3f4f6', display: 'flex', gap: 28, flexWrap: 'wrap' }}>
                <Stat label="Final rating in report" value={`${result.summary.statedFinal}%`} />
                {result.summary.expectedFinal !== null && result.summary.expectedFinal !== undefined && (
                  <Stat
                    label="Combined value of components"
                    value={`${result.summary.expectedFinal}%`}
                    warn={Math.abs(result.summary.expectedFinal - result.summary.statedFinal) > 2}
                  />
                )}
                <Stat label="Component ratings found" value={String(result.summary.componentCount ?? 0)} />
              </div>
            )}

            {result.summary?.isMentalInjury && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6', display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Mental injury — AMA Ch.14
                </div>
                {result.summary.mentalOverall !== null && result.summary.mentalOverall !== undefined && (
                  <Stat label="Overall mental impairment" value={`${result.summary.mentalOverall}%`} />
                )}
                {result.summary.bandTableSource && (
                  <div style={{ fontSize: 12, color: '#9ca3af' }}>
                    Class bands checked against {result.summary.bandTableSource}
                  </div>
                )}
              </div>
            )}

            {result.summary?.truncated && (
              <div style={{ marginTop: 14, fontSize: 12, color: '#9a3412' }}>
                The document was very long — only the first portion was reviewed.
              </div>
            )}
            {(result.summary?.partialErrors || []).map((e, i) => (
              <div key={i} style={{ marginTop: 8, fontSize: 12, color: '#9a3412' }}>{e}</div>
            ))}
          </div>

          {result.apportionmentDraft && <ApportionmentDraft draft={result.apportionmentDraft} />}

          {findings.length > 0 && (
            <>
              {/* Filters */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
                <FilterChip active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')} label={`All (${findings.filter((f) => !dismissed.has(f.id)).length})`} />
                {CATEGORIES.map((c) => (
                  <FilterChip
                    key={c.key}
                    active={categoryFilter === c.key}
                    onClick={() => setCategoryFilter(c.key)}
                    label={`${c.label} (${countFor(c.key)})`}
                    color={c.color}
                  />
                ))}
                <div style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
                <select
                  value={severityFilter}
                  onChange={(e) => setSeverityFilter(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 999, border: '1px solid #e5e7eb', fontSize: 13, color: '#374151', background: '#fff' }}
                >
                  <option value="all">All severities</option>
                  <option value="critical">Critical only</option>
                  <option value="major">Major only</option>
                  <option value="minor">Minor only</option>
                </select>
                {dismissed.size > 0 && (
                  <button
                    onClick={() => setDismissed(new Set())}
                    style={{ padding: '7px 12px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', fontSize: 13, color: '#6b7280', cursor: 'pointer' }}
                  >
                    Restore {dismissed.size} dismissed
                  </button>
                )}
              </div>

              {visibleFindings.length === 0 ? (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>
                  Nothing matches the current filters.
                </div>
              ) : (
                visibleFindings.map((f) => (
                  <FindingCard
                    key={f.id}
                    finding={f}
                    onDismiss={() => setDismissed((prev) => new Set(prev).add(f.id))}
                  />
                ))
              )}
            </>
          )}

          {findings.length === 0 && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: 28, textAlign: 'center' }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#166534' }}>No issues identified</div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#15803d' }}>
                The automated checks found nothing. This does not replace your own review of the report.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const CONCLUSION_LABEL = {
  applies: 'Apportionment applies on the evidence in the report',
  'does not apply': 'Apportionment does not apply on the evidence in the report',
  'insufficient evidence': 'The report does not contain enough to settle apportionment',
};

function ApportionmentDraft({ draft }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft.draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderLeft: '4px solid #7c3aed', borderRadius: 12, padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>Suggested apportionment paragraph</div>
          <div style={{ marginTop: 3, fontSize: 13, color: '#6b7280' }}>
            {CONCLUSION_LABEL[draft.conclusion] || draft.conclusion}
          </div>
        </div>
        <button
          onClick={copy}
          style={{
            padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
            background: copied ? '#f0fdf4' : '#fff', color: copied ? '#166534' : '#374151',
            fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {copied ? 'Copied' : 'Copy text'}
        </button>
      </div>

      <div
        style={{
          marginTop: 14, padding: '14px 16px', background: '#faf5ff', border: '1px solid #e9d5ff',
          borderRadius: 8, fontSize: 14, color: '#1f2937', lineHeight: 1.7, whiteSpace: 'pre-wrap',
        }}
      >
        {draft.draft}
      </div>

      {draft.basedOn?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6b7280', marginBottom: 6 }}>
            Drawn from these facts in the report
          </div>
          {draft.basedOn.map((f, i) => (
            <div key={i} style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}>• {f}</div>
          ))}
        </div>
      )}

      {draft.gaps?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9a3412', marginBottom: 6 }}>
            Still needed to finalise
          </div>
          {draft.gaps.map((g, i) => (
            <div key={i} style={{ fontSize: 13, color: '#9a3412', lineHeight: 1.7 }}>• {g}</div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
        Drafted from the uploaded report only. Verify every fact against the source before using it, and set the
        percentage split yourself.
      </div>
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9ca3af', fontWeight: 600 }}>
        {label}
      </div>
      <div style={{ marginTop: 3, fontSize: 20, fontWeight: 600, color: warn ? '#b91c1c' : '#111827' }}>{value}</div>
    </div>
  );
}

function FilterChip({ active, onClick, label, color = '#2563eb' }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 500, cursor: 'pointer',
        border: `1px solid ${active ? color : '#e5e7eb'}`,
        background: active ? color : '#fff',
        color: active ? '#fff' : '#374151',
      }}
    >
      {label}
    </button>
  );
}

function FindingCard({ finding, onDismiss }) {
  const style = SEVERITY_STYLE[finding.severity] || SEVERITY_STYLE.minor;
  const category = CATEGORIES.find((c) => c.key === finding.category);

  return (
    <div
      style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18, marginBottom: 12,
        borderLeft: `4px solid ${category?.color || '#9ca3af'}`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
              padding: '3px 8px', borderRadius: 4,
              background: style.bg, border: `1px solid ${style.border}`, color: style.text,
            }}
          >
            {style.label}
          </span>
          <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {category?.label || finding.category}
          </span>
          {finding.source === 'rule' && (
            <span style={{ fontSize: 10, color: '#6b7280', background: '#f3f4f6', padding: '3px 7px', borderRadius: 4 }}>
              Calculated
            </span>
          )}
        </div>
        <button
          onClick={onDismiss}
          title="Dismiss this finding"
          style={{ border: 'none', background: 'transparent', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: 2 }}
        >
          Dismiss
        </button>
      </div>

      <div style={{ marginTop: 10, fontSize: 15, fontWeight: 600, color: '#111827', lineHeight: 1.4 }}>
        {finding.title}
      </div>

      {finding.location && (
        <div style={{ marginTop: 4, fontSize: 12, color: '#9ca3af' }}>{finding.location}</div>
      )}

      {finding.detail && (
        <div style={{ marginTop: 8, fontSize: 14, color: '#4b5563', lineHeight: 1.6 }}>{finding.detail}</div>
      )}

      {finding.quote && (
        <div
          style={{
            marginTop: 10, padding: '10px 14px', background: '#f9fafb', borderLeft: '3px solid #d1d5db',
            borderRadius: '0 6px 6px 0', fontSize: 13, color: '#4b5563', fontStyle: 'italic',
            whiteSpace: 'pre-wrap', lineHeight: 1.6,
          }}
        >
          {finding.quote}
        </div>
      )}

      {Array.isArray(finding.working) && finding.working.length > 0 && (
        <div style={{ marginTop: 10, padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', marginBottom: 6 }}>
            Working
          </div>
          {finding.working.map((step, i) => (
            <div key={i} style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, fontFamily: 'ui-monospace, Menlo, monospace' }}>
              {step}
            </div>
          ))}
        </div>
      )}

      {finding.suggestion && (
        <div style={{ marginTop: 10, fontSize: 13, color: '#1e40af', background: '#eff6ff', padding: '9px 13px', borderRadius: 6, lineHeight: 1.6 }}>
          <strong>Action:</strong> {finding.suggestion}
        </div>
      )}
    </div>
  );
}
